/**
 * Pure identity helpers for the shared Axios client.
 *
 * Nothing here reads MSAL, localStorage, or the environment: the caller supplies
 * the token, the email, and the token scope. That keeps the header rules and the
 * query-param stripping rules testable in isolation (Requirements 1.1-1.4).
 */

/** Token scope decided by the existing isDatabricksEndpoint() classifier. */
export const TOKEN_SCOPE = { APP: "app", DATABRICKS: "databricks" };

/** Legacy per-scope token header mirrors kept for backward compatibility. */
const SCOPE_TOKEN_HEADER = {
  [TOKEN_SCOPE.APP]: "X-MSAL-Token",
  [TOKEN_SCOPE.DATABRICKS]: "X-Databricks-Token",
};

/** Query parameters that must never carry a token (Req 1.2). */
export const TOKEN_QUERY_PARAMS = ["app_token", "access_token", "token"];

const TOKEN_QUERY_PARAM_SET = new Set(TOKEN_QUERY_PARAMS);

/** A usable bearer token is a string with at least one character. */
function isUsableToken(token) {
  return typeof token === "string" && token.length > 0;
}

/** Trimmed email, or null when there is nothing meaningful to send. */
function readEmail(email) {
  if (typeof email !== "string") return null;
  const trimmed = email.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Unknown scopes get no legacy mirror; a missing scope defaults to app scope. */
function resolveScope(scope) {
  if (scope == null || scope === "") return TOKEN_SCOPE.APP;
  const normalized = String(scope).toLowerCase();
  if (normalized === TOKEN_SCOPE.APP) return TOKEN_SCOPE.APP;
  if (normalized === TOKEN_SCOPE.DATABRICKS) return TOKEN_SCOPE.DATABRICKS;
  return null;
}

/**
 * Builds the identity headers for one request.
 *
 * `Authorization` is `Bearer <token>` verbatim for any non-empty token string and
 * is omitted entirely otherwise, so an unauthenticated request stays
 * unauthenticated rather than sending a bare `Bearer ` (Req 1.1). `X-User-Email`
 * is present only when the trimmed email is non-empty (Req 1.3, 1.4). The legacy
 * `X-MSAL-Token` / `X-Databricks-Token` mirror for the given scope is set from
 * the same token value.
 *
 * @param {{ token?: string|null, email?: string|null, scope?: string|null }} [identity]
 * @returns {Record<string,string>} headers to merge (never contains empty values)
 */
export function resolveIdentityHeaders({ token, email, scope } = {}) {
  const headers = {};

  if (isUsableToken(token)) {
    headers.Authorization = `Bearer ${token}`;
    const mirror = SCOPE_TOKEN_HEADER[resolveScope(scope)];
    if (mirror) headers[mirror] = token;
  }

  const userEmail = readEmail(email);
  if (userEmail) headers["X-User-Email"] = userEmail;

  return headers;
}

/** Splits a URL (absolute or relative) into path, raw query, and raw hash. */
function splitUrl(url) {
  const raw = typeof url === "string" ? url : "";
  const hashIndex = raw.indexOf("#");
  const beforeHash = hashIndex === -1 ? raw : raw.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : raw.slice(hashIndex);
  const queryIndex = beforeHash.indexOf("?");

  return {
    base: queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex),
    query: queryIndex === -1 ? "" : beforeHash.slice(queryIndex + 1),
    hash,
    hasQuery: queryIndex !== -1,
  };
}

/**
 * Decodes a raw query-string key. Malformed percent escapes are compared raw
 * rather than throwing.
 */
function decodeKey(rawKey) {
  const candidate = rawKey.replace(/\+/g, " ");
  try {
    return decodeURIComponent(candidate);
  } catch {
    return rawKey;
  }
}

/** Raw `key=value` segments of a query string, preserving order and repeats. */
function querySegments(query) {
  return query === "" ? [] : query.split("&");
}

function segmentKey(segment) {
  const eqIndex = segment.indexOf("=");
  return decodeKey(eqIndex === -1 ? segment : segment.slice(0, eqIndex));
}

function isTokenKey(key) {
  return TOKEN_QUERY_PARAM_SET.has(key);
}

/** Keys carried by a params value, whether a plain object or URLSearchParams. */
function paramKeys(params) {
  if (params == null) return [];
  if (typeof URLSearchParams !== "undefined" && params instanceof URLSearchParams) {
    return Array.from(params.keys());
  }
  if (typeof params !== "object") return [];
  return Object.keys(params);
}

/**
 * True when the URL query string or the params object carries a token parameter.
 *
 * @param {string} [url]
 * @param {object|URLSearchParams} [params]
 * @returns {boolean}
 */
export function hasTokenInQuery(url, params) {
  const { query } = splitUrl(url);
  if (querySegments(query).some((segment) => isTokenKey(segmentKey(segment)))) {
    return true;
  }
  return paramKeys(params).some(isTokenKey);
}

/** Removes token keys from a params value without touching anything else. */
function stripParams(params) {
  if (params == null || typeof params !== "object") {
    return { params, removed: [] };
  }

  if (typeof URLSearchParams !== "undefined" && params instanceof URLSearchParams) {
    const removed = Array.from(params.keys()).filter(isTokenKey);
    if (removed.length === 0) return { params, removed };
    const next = new URLSearchParams();
    for (const [key, value] of params.entries()) {
      if (!isTokenKey(key)) next.append(key, value);
    }
    return { params: next, removed };
  }

  const removed = Object.keys(params).filter(isTokenKey);
  if (removed.length === 0) return { params, removed };

  const next = {};
  for (const key of Object.keys(params)) {
    if (!isTokenKey(key)) next[key] = params[key];
  }
  return { params: next, removed };
}

/**
 * Strips the legacy token query params (`app_token`, `access_token`, `token`)
 * from both the URL query string and the params object, leaving every other
 * parameter — including repeated and array-valued ones — untouched (Req 1.2).
 *
 * Works on relative URLs (the shared client uses relative paths with a baseURL),
 * leaves the path unchanged, and preserves any fragment.
 *
 * @param {string} [url]
 * @param {object|URLSearchParams} [params]
 * @returns {{ url: string, params: object|URLSearchParams|undefined, removed: string[] }}
 */
export function stripTokenQueryParams(url, params) {
  const { base, query, hash, hasQuery } = splitUrl(url);
  const segments = querySegments(query);
  const kept = [];
  const removedFromUrl = [];

  for (const segment of segments) {
    const key = segmentKey(segment);
    if (isTokenKey(key)) removedFromUrl.push(key);
    else kept.push(segment);
  }

  let nextUrl;
  if (removedFromUrl.length === 0) {
    nextUrl = typeof url === "string" ? url : "";
  } else if (kept.length > 0) {
    nextUrl = `${base}?${kept.join("&")}${hash}`;
  } else {
    // Every param was a token param: drop the now-empty query string, unless the
    // original URL had a bare "?" with nothing after it.
    nextUrl = hasQuery && query === "" ? `${base}?${hash}` : `${base}${hash}`;
  }

  const { params: nextParams, removed: removedFromParams } = stripParams(params);

  return {
    url: nextUrl,
    params: nextParams,
    removed: [...removedFromUrl, ...removedFromParams],
  };
}
